import './offer-step.scss';

import { notification } from 'antd/lib';
import { createContext, useContext, useMemo, useState } from 'react';

import { ieLeasingApi } from '@/components/App/Leasing/api';
import type { TLeasingOrderResponse } from '@/components/App/Leasing/types';
import { Button } from '@/components/Shared/ui';
import { ReloadOutlined } from '@/media/antd-icons';
import { useAppDispatch, useAppSelector } from '@/store';

import { IeLeasingOrderContext } from '../IeOrder';
import { refreshOrderData } from '../store';
import { LeasingAcceptModal } from './entities';
import { LeasingOffers } from './widgets';

interface OfferStepContextInterface {
  onAcceptLeasingOffer: (data: TLeasingOrderResponse) => void;
}
export const OfferStepContext = createContext<OfferStepContextInterface>({
  onAcceptLeasingOffer: () => {}
});

const OfferStep = () => {
  const { isActionsDisabled } = useContext(IeLeasingOrderContext);

  const { dicts } = useAppSelector((state) => state.dict);

  const { request } = useAppSelector((state) => state.ieLeasingOrder);

  const dispatch = useAppDispatch();

  const [focusedLeasingResponse, setFocusedLeasingResponse] =
    useState<TLeasingOrderResponse | null>(null);

  const [showLeasingAcceptModal, setShowLeasingAcceptModal] = useState(false);

  const [isLeasingAcceptLoading, setLeasingAcceptLoading] = useState(false);

  const isHasFinancedOffer: boolean = useMemo(() => {
    if (!request) return false;
    return request.responses.some((offer) => offer.status.code === 'financed');
  }, [request]);

  const updateResponses = async () => {
    if (isActionsDisabled) return;
    dispatch(refreshOrderData());
  };

  const onAcceptLeasingOffer = async (data: TLeasingOrderResponse) => {
    if (isActionsDisabled) return;

    setFocusedLeasingResponse(data);
    setShowLeasingAcceptModal(true);
  };

  const onSelectLeasingOffer = async () => {
    if (!focusedLeasingResponse || !dicts || isActionsDisabled) return;

    setLeasingAcceptLoading(true);

    try {
      await ieLeasingApi.acceptOffer(focusedLeasingResponse.uuid);

      dispatch(refreshOrderData());
    } catch {
      notification.error({
        title: 'Произошла ошибка',
        description:
          'Не получилось принять предложение. Пожалуйста попробуйте позже'
      });
    } finally {
      setLeasingAcceptLoading(false);
    }
  };

  return (
    <div className="ie-offer-step">
      <div className="ie-offer-step__header">
        <div className="ie-offer-step__title-wrapper">
          <h4 className="ie-offer-step__title">
            Предложения от лизинговых компаний
          </h4>
        </div>
        <div className="ie-offer-step__actions">
          {!isHasFinancedOffer && (
            <Button
              className="ie-offer-step__action"
              size="small"
              onClick={() => updateResponses()}
              isReversed
              disabled={isActionsDisabled}
            >
              <p className="ie-offer-step__button-text">Обновить</p>
              <ReloadOutlined />
            </Button>
          )}
        </div>
      </div>

      <OfferStepContext.Provider
        value={{
          onAcceptLeasingOffer
        }}
      >
        <LeasingOffers />
      </OfferStepContext.Provider>
      <LeasingAcceptModal
        focusedLeasingResponse={focusedLeasingResponse}
        showAcceptModal={showLeasingAcceptModal}
        onSelectLeasingOffer={onSelectLeasingOffer}
        onCancel={() => {
          setShowLeasingAcceptModal(false);
          setFocusedLeasingResponse(null);
        }}
        isAcceptLoading={isLeasingAcceptLoading}
      />
    </div>
  );
};

export default OfferStep;
